"use client";

import React, { createContext, useContext, ReactNode } from "react";

// Feature flags from Organization model
export interface OrganizationFeatures {
  gpsTrackingEnabled: boolean;
  timeTrackingEnabled: boolean;
  smsEnabled: boolean;
  priceBookEnabled: boolean;
  bookingEnabled: boolean;
  aiAgentEnabled: boolean;
}

// Staff permissions from Organization model
export interface StaffPermissions {
  staffCanViewAllJobs: boolean;
  staffCanViewFinancials: boolean;
}

export interface OrganizationContextValue {
  id: string;
  name: string;
  slug: string;
  logo?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  features: OrganizationFeatures;
  staffPermissions: StaffPermissions;
}

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

interface OrganizationProviderProps {
  children: ReactNode;
  value: OrganizationContextValue;
}

export function OrganizationProvider({ children, value }: OrganizationProviderProps) {
  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization(): OrganizationContextValue {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error("useOrganization must be used within OrganizationProvider");
  }
  return context;
}

// Helper hook to check if a specific feature is enabled
export function useFeature(feature: keyof OrganizationFeatures): boolean {
  const { features } = useOrganization();
  return features[feature];
}
